import * as React from "react";
import { Alert, AlertTitle, Card } from "@mui/material";
import { getErrorMsg } from "../../../../utilities/rtkQueryErrorHelpers";
import { useSearchJournalEntriesMutation } from "../../../../services/cclTokenedJournalEntryApi";
import { JournalEntry } from "../../../../services/types/journalEntryTypes";
import { useGetCclParticipantsByEventKeyQuery } from "../../../../services/cclTokenedEnterpriseParticipantApi";
import ComponentLoader from "../../../../components/common/componentLoader";
import HistoryTimeLine from "../../../../components/common/historyTimeline/historyTimeline";

interface SessionParticipantsJournalEntryTimelineCardProps {
    sessionId: string;
    sessionCode: string;
}

const SessionParticipantsJournalEntryTimelineCard: React.FC<
    SessionParticipantsJournalEntryTimelineCardProps
> = (props) => {
    const [allJournalEntries, setAllJournalEntries] = React.useState<JournalEntry[]>([]);
    const { data: participants } = useGetCclParticipantsByEventKeyQuery(props.sessionId, {
        skip: !props.sessionId,
    });
    const [
        runSearchJournalEntries,
        { isLoading: journalEntriesLoading, isError: journalEntriesIsError, error: journalEntriesError },
    ] = useSearchJournalEntriesMutation();

    React.useEffect(() => {
        if (props.sessionCode === "") return;

        runSearchJournalEntries({
            journalCategoryIds: [3], // registration journal entries
            categoryDataFieldName: "sessionCode",
            categoryDataFieldValue: props.sessionCode,
        })
            .unwrap()
            .then((entries: JournalEntry[]) => setAllJournalEntries([...entries]))
            .catch(() => setAllJournalEntries([]));
    }, [props.sessionCode, runSearchJournalEntries]);

    const entriesWithParticipantNames = React.useMemo(
        () =>
            allJournalEntries.map((entry) => {
                let esiKey: string | undefined;
                try {
                    esiKey = JSON.parse(entry.categoryData)?.esiKey;
                } catch {
                    esiKey = undefined;
                }
                const participant = participants?.find((p) => p.esiKey.toString() === esiKey);
                return participant ? { ...entry, participantName: participant.fullName } : entry;
            }),
        [allJournalEntries, participants],
    );

    if (journalEntriesIsError) {
        return (
            <div>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {getErrorMsg(journalEntriesError)}
                </Alert>
            </div>
        );
    }

    return (
        <Card sx={{ height: 1, width: { xs: 1, lg: 0.8, xl: 0.6 }, m: 2, p: 2 }}>
            {journalEntriesLoading ? (
                <ComponentLoader msg="Retrieving Participants Journal Entries..." />
            ) : (
                <HistoryTimeLine events={[]} journalEntries={entriesWithParticipantNames} />
            )}
        </Card>
    );
};

export default SessionParticipantsJournalEntryTimelineCard;
